import React, { useState } from "react";
import {
    SectionCard,
    PaginationBar,
    inputClass,
    checkboxClass,
    primaryButtonClass,
    saveButtonClass,
    mutedButtonClass,
} from "../adminShared";

const CompaniesTab = ({
    accountSearch,
    setAccountSearch,
    filteredAccounts,
    accountDrafts,
    setAccountDrafts,
    domainDrafts,
    setDomainDrafts,
    newDomainInputs,
    setNewDomainInputs,
    working,
    saveAccount,
    saveAccountDomain,
    createAccountDomain,
    setCompanyDialogOpen,
    accountsPagination,
    goToAccountsPage,
}) => {
    const [openAccountIds, setOpenAccountIds] = useState([]);

    const toggleAccount = (accountId) => {
        setOpenAccountIds((prev) =>
            prev.includes(accountId)
                ? prev.filter((id) => id !== accountId)
                : [...prev, accountId]
        );
    };

    return (
        <div className="flex flex-col gap-6">
            <SectionCard
                title="Create company"
                description="Create a company record and optionally assign its first domain."
                right={
                    <button
                        type="button"
                        className={`${primaryButtonClass} px-4`}
                        onClick={() => setCompanyDialogOpen(true)}
                    >
                        Create company
                    </button>
                }
            />

            <SectionCard
                title="Manage companies"
                description="Compact company cards with expandable domains to keep the page readable."
                right={
                    <div className="w-full sm:w-72">
                        <input
                            className={inputClass}
                            value={accountSearch}
                            onChange={(e) => setAccountSearch(e.target.value)}
                            placeholder="Search companies..."
                        />
                    </div>
                }
            >
                <div className="space-y-3">
                    {filteredAccounts.map((account) => {
                        const draft = accountDrafts[account.account_id] || {};
                        const isOpen = openAccountIds.includes(account.account_id);

                        return (
                            <div
                                key={account.account_id}
                                className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300"
                            >
                                <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_180px_96px_150px] xl:items-end">
                                    <div className="text-left">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Company name
                                        </label>
                                        <input
                                            className={`${inputClass} h-10`}
                                            value={draft.name || ""}
                                            onChange={(e) =>
                                                setAccountDrafts((prev) => ({
                                                    ...prev,
                                                    [account.account_id]: {
                                                        ...prev[account.account_id],
                                                        name: e.target.value,
                                                    },
                                                }))
                                            }
                                        />
                                    </div>

                                    <div className="text-left">
                                        <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                                            Status
                                        </label>
                                        <label className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3">
                                            <input
                                                type="checkbox"
                                                className={checkboxClass}
                                                aria-label="Company status"
                                                checked={!!draft.is_active}
                                                onChange={(e) =>
                                                    setAccountDrafts((prev) => ({
                                                        ...prev,
                                                        [account.account_id]: {
                                                            ...prev[account.account_id],
                                                            is_active: e.target.checked,
                                                        },
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>

                                    <div className="flex items-end">
                                        <button
                                            className={`${saveButtonClass} h-10 w-full`}
                                            onClick={() => saveAccount(account.account_id)}
                                            disabled={working}
                                        >
                                            Save
                                        </button>
                                    </div>

                                    <div className="flex items-center justify-start gap-2">
                                        <button
                                            type="button"
                                            className={`${mutedButtonClass} whitespace-nowrap px-4`}
                                            onClick={() => toggleAccount(account.account_id)}
                                        >
                                            {isOpen ? "Hide domains" : "Show domains"}
                                        </button>
                                    </div>
                                </div>

                                {isOpen ? (
                                    <div className="mt-4 border-t border-slate-200 pt-4">
                                        <div className="flex items-center justify-between gap-3 text-left">
                                            <div className="text-left">
                                                <div className="text-sm font-semibold text-slate-900 text-left">
                                                    Domains
                                                </div>
                                                <div className="text-xs text-slate-500 text-left">
                                                    Edit each domain in place or add a new one below.
                                                </div>
                                            </div>
                                        </div>

                                        <div className="mt-3 space-y-2">
                                            {(account.domains || []).map((domain) => {
                                                const domainDraft = domainDrafts[domain.account_domain_id] || {};
                                                return (
                                                    <div
                                                        key={domain.account_domain_id}
                                                        className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-2 lg:grid-cols-[minmax(0,1fr)_160px_88px]"
                                                    >
                                                        <input
                                                            className={`${inputClass} h-10`}
                                                            value={domainDraft.domain || ""}
                                                            onChange={(e) =>
                                                                setDomainDrafts((prev) => ({
                                                                    ...prev,
                                                                    [domain.account_domain_id]: {
                                                                        ...prev[domain.account_domain_id],
                                                                        domain: e.target.value,
                                                                    },
                                                                }))
                                                            }
                                                        />

                                                        <label className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3">
                                                            <input
                                                                type="checkbox"
                                                                className={checkboxClass}
                                                                aria-label="Domain status"
                                                                checked={!!domainDraft.is_active}
                                                                onChange={(e) =>
                                                                    setDomainDrafts((prev) => ({
                                                                        ...prev,
                                                                        [domain.account_domain_id]: {
                                                                            ...prev[domain.account_domain_id],
                                                                            is_active: e.target.checked,
                                                                        },
                                                                    }))
                                                                }
                                                            />
                                                        </label>

                                                        <button
                                                            className={`${saveButtonClass} h-10`}
                                                            onClick={() => saveAccountDomain(domain.account_domain_id)}
                                                            disabled={working}
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                );
                                            })}

                                            {(account.domains || []).length === 0 ? (
                                                <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-3 text-sm text-slate-500">
                                                    No domains yet.
                                                </div>
                                            ) : null}

                                            <div className="grid gap-2 pt-1 lg:grid-cols-[minmax(0,1fr)_120px]">
                                                <input
                                                    className={`${inputClass} h-10`}
                                                    value={newDomainInputs[account.account_id] || ""}
                                                    onChange={(e) =>
                                                        setNewDomainInputs((prev) => ({
                                                            ...prev,
                                                            [account.account_id]: e.target.value,
                                                        }))
                                                    }
                                                    placeholder="Add another domain"
                                                />
                                                <button
                                                    className={mutedButtonClass}
                                                    onClick={() => createAccountDomain(account.account_id)}
                                                    disabled={working}
                                                >
                                                    Add domain
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}

                    {filteredAccounts.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                            No companies found.
                        </div>
                    ) : null}
                </div>
                <PaginationBar
                    page={accountsPagination.page}
                    totalPages={accountsPagination.total_pages}
                    totalCount={accountsPagination.total_count}
                    totalLabel="companies"
                    onPrevious={() => goToAccountsPage(accountsPagination.page - 1)}
                    onNext={() => goToAccountsPage(accountsPagination.page + 1)}
                    disabled={working}
                />
            </SectionCard>
        </div>
    );
};

export default CompaniesTab;
